/**
 * The view cube: core's cube (`viewCubeLayout`) drawn as SVG in the 3D view's
 * bottom-left corner, turning as the camera turns and lying over the canvas
 * and every other overlay. Hovering a face, an edge or a corner lights it;
 * pressing one looks from that side; dragging turns the view as if the cube
 * were grabbed; the house icon at its lower right glides home. Only the cube
 * and the house take the pointer, so a press anywhere else in its corner
 * reaches the scene.
 */

import { css } from '@emotion/react';
import type { Point } from '@kaiizen/kai-floor-plan';
import {
  beginViewCubeDrag,
  endViewCubeDrag,
  ICON_VIEW_BOX,
  iconStrokeWidth,
  MINIMAP_MARGIN,
  moveViewCubeDrag,
  tapViewCube,
  VIEW_CUBE_STYLE,
  type ViewCubeDrag,
  type ViewCubeHit,
  viewCubeLayout,
  viewCubeOutline,
  viewCubeRegionAt,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useMemo, useRef, useState } from 'react';
import { useView3D, useViewAngle } from '../view3d';

const VIEW_CUBE_STYLE_CSS = css`
  label: VIEW_CUBE_STYLE_CSS;
  position: absolute; left: ${MINIMAP_MARGIN}px; bottom: ${MINIMAP_MARGIN}px; z-index: 44;
  pointer-events: none; user-select: none; touch-action: none;
  svg { display: block; overflow: visible; pointer-events: none; }
`;

const pointsOf = (outline: readonly Point[]): string => outline.map(({ x, y }) => `${x},${y}`).join(' ');

export function ViewCube(): React.JSX.Element | null {
  const angle = useViewAngle();
  const controls = useView3D();
  const { setCameraAngle } = controls;
  const layout = useMemo(() => angle && viewCubeLayout(angle, 'pointer'), [angle]);
  const [hovered, setHovered] = useState<ViewCubeHit | null>(null);
  const [pressed, setPressed] = useState<ViewCubeHit | null>(null);
  const dragRef = useRef<ViewCubeDrag | null>(null);
  if (!angle || !layout) return null;

  const pointOf = (event: React.PointerEvent<SVGSVGElement>): Point => {
    const { left, top } = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - left, y: event.clientY - top };
  };

  const endPress = (event: React.PointerEvent<SVGSVGElement>): ViewCubeDrag | null => {
    const drag = dragRef.current;
    dragRef.current = null;
    setPressed(null);
    event.currentTarget.releasePointerCapture(event.pointerId);
    return drag;
  };

  const lit = pressed ?? hovered;
  const highlight = lit && viewCubeOutline(layout, lit);

  return (
    <div css={VIEW_CUBE_STYLE_CSS}>
      <svg
        data-testid='view-cube'
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        style={{ cursor: lit ? 'pointer' : undefined }}
        onPointerDown={event => {
          const drag = event.button === 0 ? beginViewCubeDrag(layout, angle, pointOf(event)) : null;
          if (!drag) return;
          dragRef.current = drag;
          setPressed(drag.hit);
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={event => {
          const drag = dragRef.current;
          if (!drag) return setHovered(viewCubeRegionAt(layout, pointOf(event)));
          dragRef.current = moveViewCubeDrag(drag, pointOf(event));
          if (dragRef.current.angle) setCameraAngle(dragRef.current.angle);
        }}
        onPointerUp={event => {
          const drag = endPress(event);
          tapViewCube(drag && endViewCubeDrag(drag), controls);
        }}
        onPointerCancel={endPress}
        onPointerLeave={() => setHovered(null)}
      >
        {layout.faces.map(({ face, label, polygon, matrix, fill }) => (
          <g key={face}>
            <polygon
              data-face={face}
              points={pointsOf(polygon)}
              fill={fill}
              stroke={VIEW_CUBE_STYLE.ink}
              strokeWidth={VIEW_CUBE_STYLE.strokeWidth}
              strokeLinejoin='round'
              pointerEvents='all'
            />
            <g transform={`matrix(${matrix.join(' ')})`}>
              <text
                x={0.5}
                y={0.5}
                fontSize={VIEW_CUBE_STYLE.labelFontSize}
                fontWeight={VIEW_CUBE_STYLE.labelFontWeight}
                fill={VIEW_CUBE_STYLE.ink}
                textAnchor='middle'
                dominantBaseline='central'
              >
                {label}
              </text>
            </g>
          </g>
        ))}
        {Object.values(layout.buttons).map(({ button, icon, center, radius, box }) => (
          <g key={button} data-act={`view-cube-${button}`}>
            <circle
              cx={center.x}
              cy={center.y}
              r={radius}
              fill={VIEW_CUBE_STYLE.buttonFill}
              stroke={VIEW_CUBE_STYLE.ink}
              strokeWidth={VIEW_CUBE_STYLE.strokeWidth}
              pointerEvents='all'
            />
            <svg x={box.x} y={box.y} width={box.width} height={box.height} viewBox={ICON_VIEW_BOX}>
              {icon.map(path => (
                <path
                  key={path}
                  d={path}
                  fill='none'
                  stroke={VIEW_CUBE_STYLE.ink}
                  strokeWidth={iconStrokeWidth(box.width)}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              ))}
            </svg>
          </g>
        ))}
        {highlight && (
          <polygon
            data-testid='view-cube-highlight'
            points={pointsOf(highlight)}
            fill={VIEW_CUBE_STYLE.highlight.fill}
            opacity={VIEW_CUBE_STYLE.highlight.opacity}
          />
        )}
      </svg>
    </div>
  );
}
